import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Injector, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AnalyticsService } from '@common/services';
import { CommerceService } from '@common/services';
import { FiscalOnboardingFlagService } from '@common/services/fiscal-onboarding-flag.service';
import { PRICE_TEMPLATES_FLAG } from '@common/services/price-templates-access.service';
import { trackEvent } from '@intercom/messenger-js-sdk';
import { UserService } from '@modules/auth/services';
import protectedRoutes from '@modules/auth/services/protected-routes';
import { CreditosService } from '@modules/creditos/services/creditos.service';
import { trackCreditEvent } from '@modules/creditos/utils/credit-analytics.util';
import { FISCAL_ONBOARDING_ROUTE } from '@modules/fiscal-onboarding/constants/fiscal-onboarding.constants';
import { FiscalStatusPill } from '@modules/fiscal-onboarding/models';
import { FiscalOnboardingStateService } from '@modules/fiscal-onboarding/services/fiscal-onboarding-state.service';
import { SBRouteData, SideNavItem } from '@modules/navigation/models';
import { NavigationService, SideNavService } from '@modules/navigation/services';
import { isCreditosItem } from '@modules/navigation/utils/creditos-nav-item.util';
import { matchesNavLink } from '@modules/navigation/utils/nav-link-match.util';
import { TourOrchestratorService } from '@modules/onboarding/services';
import { casheaMethodsOf } from '@modules/pos/services/cashea.service';
import { CasheaLinkBadgeService } from '@modules/pos/services/cashea-link-badge.service';
import objectHash from 'object-hash';
import { Subscription } from 'rxjs';

const CASHEA_LINK_ROUTE = '/pos/cashea-link';

@Component({
    selector: 'sbpro-side-nav-item',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './side-nav-item-pro.component.html',
    styleUrls: ['side-nav-item-pro.component.scss'],
})
export class SideNavItemProComponent implements OnInit, OnDestroy {
    @Input() sideNavItem!: SideNavItem;
    @Input() hierarchy: string[] = [];

    isActive = false;
    collapsed = true;
    routeData!: SBRouteData;
    id!: string;
    hierarchyExtension!: string[];
    subscription: Subscription = new Subscription();
    ready = false;
    private _tourStartTimeout: ReturnType<typeof setTimeout> | null = null;

    /** Verdadero si el usuario tiene una cuota vencida (badge «EN MORA» en el ítem «Fina Presta»). */
    hasLateCredito = false;

    /** Órdenes de Cashea Link listas para finalizar: contador en el ítem y en su grupo cuando está cerrado. */
    casheaLinkReady = 0;

    /** Estado del trámite de Facturación Digital en el ítem «Fiscal» (recaudos v2); `null` sin trámite o ya activo. */
    fiscalBadge: FiscalStatusPill | null = null;

    get isCasheaLinkItem(): boolean {
        return this.sideNavItem?.link === CASHEA_LINK_ROUTE;
    }

    get isFiscalItem(): boolean {
        return this.sideNavItem?.link === FISCAL_ONBOARDING_ROUTE;
    }

    get containsCasheaLink(): boolean {
        return Boolean(this.sideNavItem?.submenu?.some(item => item.link === CASHEA_LINK_ROUTE));
    }

    get showCasheaLinkBadge(): boolean {
        return this.casheaLinkReady > 0 && (this.isCasheaLinkItem || (this.containsCasheaLink && this.collapsed));
    }

    /** Ítem «Fina Presta»: logo en lugar del ícono y nombre solo para lectores de pantalla (FINA-3378). */
    get isCreditos(): boolean {
        return isCreditosItem(this.sideNavItem);
    }

    constructor(
        private sideNavService: SideNavService,
        public navigationService: NavigationService,
        private router: Router,
        private changeDetectorRef: ChangeDetectorRef,
        private userService: UserService,
        private analyticsService: AnalyticsService,
        private commerceService: CommerceService,
        private creditosService: CreditosService,
        private tourOrchestrator: TourOrchestratorService,
        private injector: Injector,
    ) {}

    ngOnInit() {
        this.id = objectHash(this.sideNavItem);
        this.hierarchyExtension = [...this.hierarchy, this.id];

        // Handle URL changes to determine active state
        this.subscription.add(this.navigationService.currentURL$().subscribe(currentURL => this.determineIfActive(currentURL.replace(/#.*$/, ''), this.sideNavItem)));

        // Handle other menu items expanding/collapsing
        this.subscription.add(
            this.sideNavService.expand$.subscribe(ids => {
                if (!this.ready) return;

                const thisItemInExpandedHierarchy = ids.find(id => id === this.id);
                const sameLevelExpansion = ids.length === this.hierarchyExtension.length;

                // Close this menu if another one at the same level is expanded
                if (!thisItemInExpandedHierarchy && sameLevelExpansion && !this.collapsed) {
                    this.collapse();
                }
            }),
        );

        // Re-render when feature flags update (handles slow network in production)
        this.subscription.add(
            this.analyticsService.flagsUpdated$.subscribe(() => {
                this.changeDetectorRef.markForCheck();
            }),
        );

        // Se pide solo para el ítem y su grupo: el resto del menú no depende del contador.
        if (this.isCasheaLinkItem || this.containsCasheaLink) {
            this.subscription.add(
                this.injector.get(CasheaLinkBadgeService).readyCount$.subscribe(ready => {
                    this.casheaLinkReady = ready;
                    this.changeDetectorRef.markForCheck();
                }),
            );
        }

        // Recaudos fiscal v2: badge de estado del ítem «Fiscal» (por su link). El facade comparte la
        // lectura con el Home y la página; `ensureLoaded` no pide nada sin el flag ni sin sesión.
        if (this.isFiscalItem) {
            const fiscalState = this.injector.get(FiscalOnboardingStateService);
            this.subscription.add(
                fiscalState.menuBadge$.subscribe(badge => {
                    this.fiscalBadge = badge;
                    this.changeDetectorRef.markForCheck();
                }),
            );
            fiscalState.ensureLoaded();
            // El flag de PostHog puede llegar tarde (~1.5 s): se reintenta al actualizarse.
            this.subscription.add(this.analyticsService.flagsUpdated$.subscribe(() => fiscalState.ensureLoaded()));
        }

        // Suscripción al estado de mora del crédito (solo el ítem «Fina Presta», por su link)
        if (isCreditosItem(this.sideNavItem)) {
            this.subscription.add(
                this.creditosService.hasLateCredito$.subscribe(hasLate => {
                    this.hasLateCredito = hasLate;
                    this.changeDetectorRef.markForCheck();
                }),
            );
        }

        // Estado inicial antes del primer pintado, para que el grupo aparezca abierto sin animar.
        // `currentURL$` es un BehaviorSubject: `isActive` ya quedó resuelto arriba.
        this.collapsed = !this.sideNavService.isExpanded(this.id) && !this.isActive;
        this.ready = true;
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
        if (this._tourStartTimeout) clearTimeout(this._tourStartTimeout);
    }

    determineIfActive(url: string, sideNavItem: SideNavItem) {
        // Check if this item or any of its children match the current URL
        this.isActive = this.isItemActive(url, sideNavItem);

        // If this item became active and we're ready, expand it
        if (this.isActive && this.ready && this.collapsed) {
            this.expand();
        }

        this.changeDetectorRef.markForCheck();
    }

    isItemActive(url: string, item: SideNavItem): boolean {
        const links = [item.link, ...(item.activeFor ?? [])].filter((link): link is string => !!link);
        if (links.some(link => matchesNavLink(url, link))) {
            return true;
        }

        // Check if any submenu items match
        if (item.submenu) {
            for (const submenu of item.submenu) {
                if (this.isItemActive(url, submenu)) {
                    return true;
                }
            }
        }

        return false;
    }

    toggle() {
        // Si YA hay un Blackout activo, este toggle NO proviene del usuario sino del hook `makeRevealSidebarItem`
        // del tour en curso, que hace clic en el `<a>` del acordeón padre SOLO para expandirlo y poder medir el
        // spotlight del ítem anidado. En ese caso NO debemos disparar la "navegación al primer hijo" de la tarea
        // de ESTE ítem (p. ej. "Pedidos" → "Nuevo pedido" = /pos/direct-sales), que sacaría al usuario de la
        // pantalla del tour (bug: el tour de "Precios de venta" terminaba abriendo un pedido). Solo expandir/colapsar.
        const blackoutActive = this.tourOrchestrator.currentState.isActive;

        // Solo desviamos al tour si la tarea de onboarding está PENDIENTE y es de la FASE ACTIVA. Ítems como
        // Pedidos/Inventario/Cuentas tienen `tourId` fijo pero son acordeones: post-completación (o tenants
        // sin onboarding) deben comportarse normal (expandir/colapsar o navegar), no forzar la navegación al
        // primer hijo. La fase se valida AQUÍ (no solo en startTourByTaskId, que corre DESPUÉS de navegar):
        // con una tarea pendiente de otra fase, navegar a "Nuevo pedido" creaba un pedido y el tour igual se
        // omitía por la barrera de fase.
        if (!blackoutActive && this.sideNavItem.tourId && this.tourOrchestrator.isTaskPendingInActivePhase(this.sideNavItem.tourId)) {
            const tourId = this.sideNavItem.tourId;

            // Si es un menú padre (acordeón como Pedidos o Cuentas sin link directo)
            if (!this.sideNavItem.link && this.sideNavItem.submenu && this.sideNavItem.submenu.length > 0) {
                const firstChildLink = this.sideNavItem.submenu[0].link;
                if (firstChildLink) {
                    // Raíz de módulo destino (primer segmento del link), para validar antes de arrancar el tour.
                    const expectedRoot = firstChildLink.split('/').filter(Boolean)[0] ?? '';
                    // 1. Promesa: Navegamos primero
                    this.router.navigate([firstChildLink]).then(success => {
                        // Si un guard canceló la navegación, no arrancar el tour en el contexto equivocado.
                        if (!success) return;
                        // 2. Delay: Damos 500ms al motor para pintar el nuevo componente
                        this._tourStartTimeout = setTimeout(() => {
                            // Guarda anti-race: el setTimeout es ciego a navegaciones posteriores. Si el usuario
                            // se fue a otro módulo dentro de esos 500ms (otro link, back del navegador), NO
                            // arrancamos el tour en la página equivocada. El kill-switch del orquestador no puede
                            // cancelarlo porque ese NavigationEnd ocurrió ANTES de que existiera `_pendingSub`.
                            const currentRoot = this.router.url.split('/').filter(Boolean)[0] ?? '';
                            if (currentRoot === expectedRoot) {
                                this.tourOrchestrator.startTourByTaskId(tourId);
                            }
                        }, 500);
                    });
                }
            } else {
                // Ítem hoja con tourId + link: navegar primero (guardando cache) y luego arrancar el tour.
                this.sideNavService.saveCache(this.hierarchyExtension, this.sideNavItem.link);
                this.router.navigate([this.sideNavItem.link]).then(success => {
                    if (!success) return; // guard canceló la navegación → no arrancar el tour
                    this.tourOrchestrator.startTourByTaskId(tourId);
                });
            }
        } else if (!blackoutActive && this.sideNavItem.tourId && !this.sideNavItem.link && this.sideNavItem.submenu && this.sideNavItem.submenu.length > 0) {
            // FINA-3295 (CB-14): clic TEMPRANO en un acordeón padre con tourId (ej. "Inventario") cuando el
            // estado del onboarding aún no cargó (API lenta): `isTaskPendingInActivePhase` dio false solo
            // porque el HTTP no volvió, y la intención de arrancar su tour se perdía (la intención diferida de
            // 3295-B solo cubría ítems-enlace). El orquestador la recuerda y, al llegar el estado, navega al
            // primer hijo y arranca el tour SI la tarea aplica y el usuario sigue en la misma página. Con
            // estado ya cargado es un no-op (la condición de arriba ya decidió de verdad). El acordeón se
            // expande/colapsa normal mientras tanto (sin return).
            const firstChildLink = this.sideNavItem.submenu[0].link;
            if (firstChildLink) {
                this.tourOrchestrator.rememberParentTourIntent(this.sideNavItem.tourId, firstChildLink);
            }
        } else if (this.sideNavItem.link) {
            // Handle link navigation
            this.sideNavService.saveCache(this.hierarchyExtension, this.sideNavItem.link);
            this.analyticsService.analyticsCapture(`navigation_${this.sideNavItem.text}`);
            // Fina Presta (FINA-3412): atado al link, nunca al texto, como el badge «EN MORA».
            if (isCreditosItem(this.sideNavItem)) {
                trackCreditEvent(this.analyticsService, 'navFinaPrestaClicked', { has_late_credit: this.hasLateCredito });
            }

            // Special cases
            if (this.sideNavItem.text === 'Nuevo pedido') {
                this.analyticsService.analyticsCapture('sale_opened sale', { lugar: 'sidenav-menu' });
            }
            if (this.sideNavItem.text === 'Caja') {
                this.analyticsService.analyticsCapture('cashbox', { lugar: 'sidenav-menu' });
            }
            if (this.sideNavItem.text === 'Utilidad/Perdida') {
                trackEvent('view_utility');
            }

            // External link handling
            if (this.sideNavItem.target === '_blank' && this.sideNavItem.link.startsWith('http')) {
                window.open(this.sideNavItem.link, '_blank');
                this.navigationService.closeSideNavIfOpenInMobile();
                return;
            }

            // Onboarding (Cero Cables): si esta ruta es el destino de una tarea PENDIENTE con auto_start,
            // encolamos su tour para que arranque al aterrizar (clic manual en el ítem del sidebar). El
            // orquestador resuelve la tarea por el link (match exacto); el sidebar solo pasa la ruta.
            const tourRequested = this.tourOrchestrator.requestTourForNavLink(this.sideNavItem.link);

            // En móvil NO cerramos el menú hamburguesa si se encoló un tour: el paso 1 apunta al ítem del
            // sidebar y necesita el menú ABIERTO. El orquestador lo cierra en el paso 2 (hook del botón Ayuda).
            if (!tourRequested) {
                this.navigationService.closeSideNavIfOpenInMobile();
            }
            return this.router.navigate([this.sideNavItem.link]);
        }

        if (!this.ready) {
            return;
        }

        this.collapsed ? this.expand() : this.collapse();
    }

    // La animación la hace el CSS (`.sidenav-menu-nested`); aquí solo cambia el estado.
    collapse() {
        if (!this.ready) return;

        this.sideNavService.setExpanded(this.id, false);
        this.collapsed = true;
        this.changeDetectorRef.markForCheck();
    }

    expand() {
        if (!this.ready) return;

        this.sideNavService.setExpanded(this.id, true);
        this.collapsed = false;

        // Notify other items at the same level
        this.sideNavService.expand$.next(this.hierarchyExtension);

        this.changeDetectorRef.markForCheck();
    }

    hideTab(submenu: any): boolean {
        // Check if this is the "Cierre de caja" menu item and user has permission
        if (submenu.text === 'Cierre de caja') {
            const userPermissions: any = this.userService.user$.getValue().permissions;
            return !userPermissions.COMMERCE?.includes('enable-cashbox');
        }

        if (submenu.text === 'Servicios') {
            // if (!this.analyticsService.getExperimentVariant('company-services')) {
            //     return true;
            // }
            return false;
        }

        // Onboarding fiscal: «Fiscal» solo con el feature flag `fiscal-recaudos` Y para el dueño del comercio
        // (o una sesión de soporte), mismo criterio que FiscalOnboardingAccessGuard en la ruta `/fiscal`.
        // Fail-closed si el flag no cargó. Se resuelve por el injector para no engordar el constructor.
        if (submenu.link === FISCAL_ONBOARDING_ROUTE && !this.injector.get(FiscalOnboardingFlagService).canAccessSync()) {
            return true;
        }

        // Cashea Link solo existe para comercios con la integración activa y Link habilitado.
        if (submenu.link === '/pos/cashea-link') {
            const tenant = this.userService.tenant$.getValue();
            if (!tenant?.cashea?.enabled || !casheaMethodsOf(tenant).link) return true;
        }

        // Módulo de Cotizaciones solo con el feature flag `cotizacionesv2` (mismo criterio que
        // PriceTemplatesAccessGuard). Fail-closed si el flag no cargó.
        if (submenu.link === '/pos/cotizaciones' && !this.analyticsService.getExperimentVariant(PRICE_TEMPLATES_FLAG)) {
            return true;
        }

        if (submenu.link) {
            const matchs = protectedRoutes.filter(pr => pr.route === submenu.link);

            if (matchs.length > 0) {
                const userPermissions: any = this.userService.user$.getValue().permissions;
                const match: boolean = userPermissions[`${matchs[0].module}`]?.includes(matchs[0]?.permission);
                return !match;
            }
        }
        return false;
    }

    /**
     * Visibility para items TOP-LEVEL (a diferencia de `hideTab` que es para submenu).
     * FINA-2062: el item «Fina Presta» (link `/creditos/general`) se muestra a CUALQUIER user
     * del tenant que tenga `creditAccess === true` (el admin asignó al menos un crédito al
     * tenant). El non-owner entra al módulo pero ve contenido blureado + overlay candado /
     * modal sin CTAs (modo viewer) — el `creditos-main` container es quien decide la UI.
     */
    isItemHidden(item: SideNavItem): boolean {
        if (!item) return false;
        if (isCreditosItem(item)) {
            const user: any = this.userService.user$.getValue();
            return !user?.creditAccess;
        }
        return false;
    }

    public getTourElementId(text: string): string {
        if (!text) return '';
        // Normaliza a un slug seguro para `id`/`data-tour`: quita acentos (NFD + strip diacríticos) y colapsa
        // cualquier carácter no alfanumérico a '-'. Debe coincidir con el `target_selector` del blueprint.
        // Para los ítems actuales ("Pedidos"/"Inventario"/"Cuentas") el resultado es idéntico al anterior;
        // esto blinda ante futuros ítems con texto acentuado o con símbolos (ñ, é, /, (, )).
        const slug = text
            .normalize('NFD')
            .replace(/[̀-ͯ]/g, '')
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '');
        return 'nav-' + slug;
    }
}
